<template>
  <v-app id="app">
    <app-modal
      v-model="openExit"
      title="Deseja realmente sair?"
      icon="mdi-exit-to-app"
      :max-width="480"
      persistent
    >
      <template #actions>
        <v-btn text @click="openExit = false">Não</v-btn>
        <v-btn dark color="primary" depressed @click="handleLogout">Sim</v-btn>
      </template>
    </app-modal>

    <v-navigation-drawer
      v-if="showAppShell"
      v-model="drawer"
      app
      class="app-sidebar no-print"
      :width="sidebarWidth"
      :mobile-breakpoint="960"
    >
      <div class="app-sidebar__header">
        <div class="app-sidebar__profile">
          <div class="app-sidebar__avatar" :class="{ 'app-sidebar__avatar--premium': sessionInfo.isPremium }">
            {{ userInitials }}
          </div>
          <div class="app-sidebar__user">
            <p class="app-sidebar__platform">Parceiros</p>
            <p class="app-sidebar__name">{{ sessionInfo.name }}</p>
            <span
              class="app-sidebar__plan"
              :class="{ 'app-sidebar__plan--premium': sessionInfo.isPremium }"
            >
              <v-icon v-if="sessionInfo.isPremium" x-small class="app-sidebar__plan-icon">mdi-crown</v-icon>
              Plano {{ sessionInfo.isPremium ? "Premium" : "Free" }}
            </span>
          </div>
        </div>
        <div class="app-sidebar__logo">
          <v-img width="64" height="52" contain :src="logo" alt="BRPoints" class="app-sidebar__logo-img" />
        </div>
      </div>

      <div class="app-sidebar__nav">
        <div v-for="group in menuGroups" :key="group.label" class="app-sidebar__group">
          <p class="app-sidebar__group-label">{{ group.label }}</p>
          <v-list class="app-sidebar__list" nav dense>
            <v-list-item
              v-for="item in group.items"
              :key="item.title"
              :to="item.href ? undefined : item.to"
              :href="item.href"
              link
              :target="item.href ? '_blank' : undefined"
              :rel="item.href ? 'noopener noreferrer' : undefined"
              class="app-sidebar__item no-print"
              :class="{ 'app-sidebar__item--active': isNavItemActive($route.path, item) }"
            >
              <v-list-item-icon class="app-sidebar__icon-wrap">
                <v-icon
                  :class="[
                    'app-sidebar__icon',
                    isNavItemActive($route.path, item)
                      ? 'app-sidebar__icon--active'
                      : 'app-sidebar__icon--inactive',
                  ]"
                >
                  {{ item.icon }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title
                  :class="[
                    'app-sidebar__label',
                    isNavItemActive($route.path, item)
                      ? 'app-sidebar__label--active'
                      : 'app-sidebar__label--inactive',
                  ]"
                >
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="isNavItemActive($route.path, item)">
                <v-icon color="primary" size="14">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>
      </div>

      <template #append>
        <v-list-item
          v-if="shouldShowPremiumPromo"
          class="app-sidebar__premium no-print"
          @click="handlePremiumUpgrade"
        >
          <v-list-item-icon class="app-sidebar__icon-wrap">
            <v-icon class="app-sidebar__premium-icon" size="22">mdi-crown</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="app-sidebar__premium-label">
              Seja Parceiro Premium
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-navigation-drawer>

    <v-app-bar
      v-if="showAppShell"
      app
      flat
      color="white"
      :height="topbarHeight"
      class="app-topbar no-print"
    >
      <v-app-bar-nav-icon class="app-topbar__menu-btn" @click="drawer = !drawer">
        <v-icon color="primary">mdi-menu</v-icon>
      </v-app-bar-nav-icon>

      <app-breadcrumb :current="pageTitle" :parent="breadcrumbParent" />

      <v-spacer />

      <div v-if="sessionInfo.name" class="app-topbar__user d-none d-md-flex">
        <span class="app-topbar__user-name">{{ sessionInfo.name }}</span>
        <span
          class="app-topbar__user-plan"
          :class="{ 'app-topbar__user-plan--premium': sessionInfo.isPremium }"
        >
          {{ sessionInfo.isPremium ? "Premium" : "Free" }}
        </span>
      </div>

      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            class="app-topbar__logout"
            aria-label="Sair"
            v-bind="attrs"
            v-on="on"
            @click="openExit = true"
          >
            <v-icon color="var(--br-icon-muted)">mdi-logout</v-icon>
          </v-btn>
        </template>
        Sair da conta
      </v-tooltip>
    </v-app-bar>

    <v-main class="app-main">
      <v-snackbar
        v-model="snackbar.active"
        :color="snackbar.color"
        :right="true"
        :top="true"
        :timeout="snackbar.timer"
      >
        {{ snackbar.message }}
        <template #action="{ attrs }">
          <v-icon color="white" v-bind="attrs" @click="snackbar.active = false">mdi-close</v-icon>
        </template>
      </v-snackbar>

      <page-loading
        v-if="shouldShowSessionLoading"
        variant="fullscreen"
        message="Carregando seus dados"
        subtitle="Estamos preparando seu painel"
        :show-preview="false"
      />

      <div v-else :class="contentClass">
        <router-view />
      </div>

      <premium-modal
        v-model="premiumModalOpen"
        :customer-document.sync="premiumCustomerDocument"
        :loading="premiumPaymentLoading"
        :checking-premium="checkingPremium"
        :qrcode-image="premiumQrcodeImage"
        :qrcode-text="premiumQrcodeText"
        :transaction-id="premiumTransactionId"
        cta-text="Liberar o Premium e ampliar suas vendas"
        cta-description="Pagamento seguro e ativação imediata."
        @pay="handlePremiumPayment"
        @checkPremium="handleCheckPremiumPaid"
      />
    </v-main>
  </v-app>
</template>

<script>
import { mapState } from "vuex";
import PageLoading from "@/components/loading/PageLoading.vue";
import PremiumModal from "@/components/premium/PremiumModal.vue";
import AppModal from "@/components/ui/AppModal.vue";
import AppBreadcrumb from "@/components/ui/AppBreadcrumb.vue";
import {
  MENU_GROUPS,
  PUBLIC_ROUTE_NAMES,
  getRouteBreadcrumb,
  getRouteTitle,
  isNavItemActive,
} from "@/config/navigation";
import { CreateTransactionSignature } from "@/services/transaction/CreateTransactionSignature";
import QRCode from "qrcode";
import { buildTransactionQRCodeText } from "@/utils/transactionQrcode";

export default {
  name: "App",

  components: { AppBreadcrumb, AppModal, PremiumModal, PageLoading },

  data: () => ({
    openExit: false,
    drawer: null,
    menuGroups: MENU_GROUPS,
    sidebarWidth: 280,
    topbarHeight: 70,
    logo: require("../src/assets/logo.webp"),
    partner: {
      name: "",
      nickname: "",
      balance: 0,
      document: "",
      digitalAccountDocument: "",
    },
    partnerDocument: "",
    premiumCustomerDocument: "",
    premiumQrcodeImage: "",
    premiumQrcodeText: "",
    premiumTransactionId: "",
    premiumPaymentLoading: false,
    checkingPremium: false,
  }),

  computed: {
    ...mapState(["snackbar", "sessionInfo", "sessionLoading", "partnerSession"]),

    showAppShell() {
      return !PUBLIC_ROUTE_NAMES.has(String(this.$route.name || ""));
    },

    shouldShowSessionLoading() {
      if (PUBLIC_ROUTE_NAMES.has(String(this.$route.name || ""))) {
        return false;
      }
      const hasTokens = !!localStorage.getItem("tokens");
      return hasTokens && (this.sessionLoading || !this.partnerSession);
    },

    shouldShowPremiumPromo() {
      return !this.sessionInfo.isPremium;
    },

    premiumModalOpen: {
      get() {
        return this.$store.state.premiumModalVisible;
      },
      set(value) {
        this.$store.commit("SET_PREMIUM_MODAL_VISIBILITY", value);
      },
    },

    pageTitle() {
      const routeName = this.$route?.name;
      if (routeName && this.$route?.meta?.title) {
        return this.$route.meta.title;
      }
      return getRouteTitle(String(routeName || ""));
    },

    breadcrumbParent() {
      return getRouteBreadcrumb(String(this.$route?.name || ""));
    },

    contentClass() {
      if (PUBLIC_ROUTE_NAMES.has(String(this.$route.name || ""))) {
        return "";
      }
      if (this.$route.name === "pdv") {
        return "br-app-content br-app-content--fluid";
      }
      return "br-app-content br-page";
    },

    userInitials() {
      const name = String(this.sessionInfo?.name || "").trim();
      if (!name) return "BR";
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
  },

  methods: {
    isNavItemActive,

    handleLogout() {
      this.openExit = false;
      this.$store.dispatch("clearSession");
      localStorage.removeItem("tokens");
      this.$router.push("/login");
    },

    handlePremiumUpgrade() {
      this.$store.commit("SET_PREMIUM_MODAL_VISIBILITY", true);
    },

    syncPartnerFromStore() {
      const partner = this.$store.state.partnerSession;
      if (!partner) return;

      this.partner.name = partner.name || "";
      this.partner.nickname = partner.nickname || partner.nickName || "";
      this.partner.balance = partner.balance || 0;
      this.partner.document = partner.document || "";
      this.partner.digitalAccountDocument = partner.digitalAccountDocument || "";
      this.partnerDocument =
        partner.digitalAccountDocument ||
        partner.document ||
        this.partnerDocument;
      if (!this.premiumCustomerDocument) {
        this.premiumCustomerDocument = this.partnerDocument;
      }
    },

    async handlePremiumPayment() {
      if (this.premiumPaymentLoading) return;
      const rawDocument =
        this.premiumCustomerDocument ||
        this.partnerDocument ||
        this.partner.digitalAccountDocument ||
        this.partner.document ||
        "";
      const sanitized = rawDocument.replace(/\D/g, "");
      if (sanitized.length !== 11) {
        this.$store.dispatch("showSnack", {
          message: "CPF inválido. Confirme os dados do cadastro.",
          color: "error",
          timer: 6000,
        });
        return;
      }
      this.premiumPaymentLoading = true;
      try {
        const signatureService = new CreateTransactionSignature();
        const signature = await signatureService.execute(sanitized);
        await this.generatePremiumQrcode(signature, sanitized);
        this.$store.dispatch("showSnack", {
          message: "Pagamento solicitado. QR code pronto para pagamento.",
          color: "success",
          timer: 5000,
        });
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Não foi possível gerar a assinatura agora.";
        this.$store.dispatch("showSnack", {
          message,
          color: "error",
        });
      } finally {
        this.premiumPaymentLoading = false;
      }
    },

    async handleCheckPremiumPaid() {
      this.checkingPremium = true;
      try {
        await this.$store.dispatch("loadSession", { force: true });
        this.syncPartnerFromStore();
        const partner = this.$store.state.partnerSession;
        if (partner?.isPremium) {
          this.$store.commit("SET_PREMIUM_MODAL_VISIBILITY", false);
          this.$store.dispatch("showSnack", {
            message: "Bem-vindo ao Premium! Seu plano foi ativado.",
            color: "success",
            timer: 5000,
          });
        } else {
          this.$store.dispatch("showSnack", {
            message: "Ainda não identificamos o pagamento. Tente novamente em instantes.",
            color: "warning",
            timer: 5000,
          });
        }
      } catch (error) {
        this.$store.dispatch("showSnack", {
          message: "Erro ao verificar o status. Tente novamente.",
          color: "error",
        });
      } finally {
        this.checkingPremium = false;
      }
    },

    async generatePremiumQrcode(signature, fallbackDocument) {
      try {
        const customerDocument =
          signature?.customer?.document || fallbackDocument;
        const qrcodeText = buildTransactionQRCodeText(signature);
        const qrcodeImage = qrcodeText
          ? await QRCode.toDataURL(qrcodeText)
          : "";
        this.premiumQrcodeImage = qrcodeImage;
        this.premiumQrcodeText = qrcodeText;
        this.premiumTransactionId = signature?.transactionId || "";
        this.premiumCustomerDocument =
          signature?.customer?.document || customerDocument;
      } catch (error) {
        this.$store.dispatch("showSnack", {
          message: "Erro ao gerar o QR code premium.",
          color: "error",
        });
      }
    },
  },

  mounted() {
    if (localStorage.getItem("tokens") && this.$store.state.partnerSession) {
      this.syncPartnerFromStore();
    }
  },

  watch: {
    partnerSession: {
      immediate: true,
      handler(partner) {
        if (partner) {
          this.syncPartnerFromStore();
        }
      },
    },

    premiumModalOpen(newVal) {
      if (!newVal) {
        this.premiumQrcodeImage = "";
        this.premiumQrcodeText = "";
        this.premiumTransactionId = "";
        this.premiumCustomerDocument = this.partnerDocument;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.app-sidebar {
  background: $br-bg-elevated !important;
  border-right: 1px solid $br-border-soft !important;
  box-shadow: 8px 0 32px rgba(23, 29, 52, 0.07) !important;
}

.app-sidebar__header {
  position: relative;
  padding: $br-space-5 $br-space-4 $br-space-4;
  background:
    radial-gradient(circle at 90% 10%, rgba(255, 255, 255, 0.22), transparent 42%),
    linear-gradient(145deg, $br-brand-light 0%, $br-brand 42%, $br-brand-darker 100%);
  color: #fff;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: auto -20% -40% auto;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }
}

.app-sidebar__profile {
  display: flex;
  align-items: center;
  gap: $br-space-3;
  margin-bottom: $br-space-4;
  text-align: left;
  position: relative;
  z-index: 1;
}

.app-sidebar__avatar {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: $br-brand;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.16);
}

.app-sidebar__avatar--premium {
  background: linear-gradient(135deg, #fff8e8 0%, #fff 100%);
  box-shadow: 0 0 0 2px rgba(255, 215, 100, 0.55), 0 4px 14px rgba(0, 0, 0, 0.16);
}

.app-sidebar__logo {
  display: flex;
  justify-content: center;
  padding-top: $br-space-2;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
  position: relative;
  z-index: 1;
}

.app-sidebar__logo-img {
  filter: brightness(0) invert(1);
}

.app-sidebar__user {
  min-width: 0;
  flex: 1;
}

.app-sidebar__platform {
  margin: 0;
  font-size: $br-font-size-xs;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  opacity: 0.82;
}

.app-sidebar__name {
  margin: 2px 0 0;
  font-size: $br-font-size-md;
  font-weight: 800;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-sidebar__plan {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: $br-space-1;
  padding: 3px 10px;
  border-radius: $br-radius-pill;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.3px;
  color: $br-brand;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.app-sidebar__plan--premium {
  color: #8a6d00;
  background: linear-gradient(135deg, #fff3c4 0%, #ffeaa0 100%);
}

.app-sidebar__plan-icon {
  color: inherit !important;
}

.app-sidebar__nav {
  padding: $br-space-3 0 $br-space-2;
}

.app-sidebar__group {
  margin-bottom: $br-space-2;
}

.app-sidebar__group-label {
  margin: 0;
  padding: $br-space-3 $br-space-4 $br-space-1;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.65px;
  text-transform: uppercase;
  color: $br-placeholder;
}

.app-sidebar__list {
  padding: 0 $br-space-2 !important;
  background: transparent !important;
}

.app-sidebar__item {
  margin-bottom: 3px !important;
  border-radius: $br-radius-md !important;
  min-height: 46px !important;
  transition: background $br-transition-base, box-shadow $br-transition-base,
    transform $br-transition-base;

  &::before {
    opacity: 0 !important;
  }

  &:hover {
    background: rgba(229, 24, 61, 0.06) !important;
    transform: translateX(2px);
  }
}

.app-sidebar__item--active {
  background: linear-gradient(90deg, rgba(229, 24, 61, 0.12), rgba(229, 24, 61, 0.05)) !important;
  box-shadow: inset 3px 0 0 $br-brand, 0 4px 12px rgba(229, 24, 61, 0.08);
}

.app-sidebar__icon-wrap {
  margin-right: $br-space-3 !important;
  min-width: 36px !important;
}

.app-sidebar__icon {
  font-size: 21px !important;
  transition: color $br-transition-fast, transform $br-transition-fast;
}

.app-sidebar__item--active .app-sidebar__icon {
  transform: scale(1.06);
}

.app-sidebar__icon--active {
  color: $br-brand !important;
}

.app-sidebar__icon--inactive {
  color: $br-nav-icon !important;
}

.app-sidebar__label {
  font-size: $br-font-size-sm !important;
  font-weight: 600 !important;
  letter-spacing: 0.15px;
}

.app-sidebar__label--active {
  color: $br-brand !important;
  font-weight: 800 !important;
}

.app-sidebar__label--inactive {
  color: $br-nav-label !important;
}

.app-sidebar__premium {
  margin: $br-space-2 $br-space-3 $br-space-3 !important;
  border-radius: $br-radius-md !important;
  background: linear-gradient(135deg, $br-premium-blush 0%, #f3d6db 100%) !important;
  border: 1px solid rgba(229, 24, 61, 0.12);
  box-shadow: 0 4px 14px rgba(229, 24, 61, 0.1);
  transition: transform $br-transition-base, box-shadow $br-transition-base;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(229, 24, 61, 0.16);
  }
}

.app-sidebar__premium-icon,
.app-sidebar__premium-label {
  color: $br-brand !important;
}

.app-sidebar__premium-label {
  font-weight: 800 !important;
  font-size: $br-font-size-sm !important;
}

.app-topbar {
  border-bottom: 1px solid $br-border-soft !important;
  box-shadow: 0 4px 18px rgba(23, 29, 52, 0.04) !important;
  background: rgba(255, 255, 255, 0.92) !important;
  backdrop-filter: blur(10px);
}

.app-topbar__user {
  display: flex;
  align-items: center;
  gap: $br-space-2;
  margin-right: $br-space-2;
  padding: 6px 12px;
  border-radius: $br-radius-pill;
  background: $br-bg-subtle;
  border: 1px solid $br-border-soft;
}

.app-topbar__user-name {
  font-size: $br-font-size-sm;
  font-weight: 700;
  color: $br-ink-soft;
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-topbar__user-plan {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: $br-radius-pill;
  color: $br-muted;
  background: rgba(36, 41, 104, 0.06);
}

.app-topbar__user-plan--premium {
  color: #8a6d00;
  background: linear-gradient(135deg, #fff3c4 0%, #ffeaa0 100%);
}

.app-topbar__menu-btn,
.app-topbar__logout {
  transition: transform $br-transition-fast, background $br-transition-fast;

  &:hover {
    transform: scale(1.06);
    background: $br-brand-soft !important;
  }
}

.app-main {
  background: $br-bg-mesh !important;
}

.br-page {
  color: $br-ink;
}

@media print {
  .no-print {
    display: none !important;
  }
}

@media (max-width: 959px) {
  .app-sidebar {
    width: 260px !important;
  }
}
</style>
